import { Schema } from "effect";
import {
  isEmailChannel,
  isMetaDmChannel,
  type EmailChannel,
  type MetaDmChannel,
} from "../channel-utils";
import type { MessageChannel } from "../message";

/**
 * Shared block-rule shape used by both the merchant-app composer (client) and
 * the message-send pipeline (server).
 *
 * The composer surfaces blocks immediately for UX; the server re-evaluates the
 * same rules at send time so a stale tab can't bypass them. Reasons must match
 * across sides so analytics and error reporting line up.
 */
export const BlockRuleReason = Schema.Literal(
  "email-integration-missing",
  "email-integration-permissions-revoked",
  "meta-seven-day-window",
  "meta-dm-character-limit",
  "email-no-recipient",
);
export type BlockRuleReason = typeof BlockRuleReason.Type;

/**
 * Optional follow-up action surfaced alongside a block, e.g. "Manage
 * integrations" → `/settings/channels/supportEmail`. The `href` is router
 * relative; clients render it as a Link, server-side handlers ignore it (the
 * UI presents the action when the same reason is rendered locally).
 */
export const BlockRuleAction = Schema.Struct({
  label: Schema.String,
  href: Schema.String,
});
export type BlockRuleAction = typeof BlockRuleAction.Type;

export const BlockedSendResult = Schema.Struct({
  reason: BlockRuleReason,
  message: Schema.String,
  action: Schema.optional(BlockRuleAction),
});
export type BlockedSendResult = typeof BlockedSendResult.Type;

export interface EmailSendBlockIntegration {
  integrationType: EmailChannel;
  permissionsRevoked?: boolean | null;
}

export interface MetaSendBlockMessage {
  channel: string;
  senderType: string;
  sentAt: Date | string;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const EMAIL_SETTINGS_HREF = "/settings/channels/supportEmail";
const MANAGE_INTEGRATIONS_LABEL = "Manage integrations";
export const META_DM_MAX_CHARACTERS = 1000;

const META_DM_CHANNEL_LABEL: Record<MetaDmChannel, string> = {
  facebookDm: "Facebook",
  instagramDm: "Instagram",
};

export function evaluateEmailSendBlock(input: {
  channel: MessageChannel;
  integration: EmailSendBlockIntegration | null | undefined;
}): BlockedSendResult | null {
  if (!isEmailChannel(input.channel)) {
    return null;
  }
  if (input.integration === undefined) {
    return null;
  }
  if (input.integration === null) {
    return {
      reason: "email-integration-missing",
      message:
        "Email integration not found. The email account for this support ticket is no longer connected.",
      action: { label: MANAGE_INTEGRATIONS_LABEL, href: EMAIL_SETTINGS_HREF },
    };
  }
  if (!input.integration.permissionsRevoked) {
    return null;
  }

  const provider =
    input.channel === "gmail"
      ? "Gmail"
      : input.channel === "outlook"
        ? "Outlook"
        : "Custom Email";
  return {
    reason: "email-integration-permissions-revoked",
    message: `${provider} integration has lost permissions. Please reconnect your ${provider} account in email settings.`,
    action: { label: MANAGE_INTEGRATIONS_LABEL, href: EMAIL_SETTINGS_HREF },
  };
}

export function evaluateEmailNoRecipientBlock(input: {
  channel: MessageChannel;
  hasRecipient: boolean;
}): BlockedSendResult | null {
  if (!isEmailChannel(input.channel)) {
    return null;
  }
  if (input.hasRecipient) {
    return null;
  }
  return {
    reason: "email-no-recipient",
    message: "Please add at least one recipient before sending this email.",
  };
}

export function evaluateMetaSevenDaySendBlock(input: {
  channel: MessageChannel;
  messages: readonly MetaSendBlockMessage[];
  messagesLoading: boolean;
  now?: number;
}): BlockedSendResult | null {
  if (!isMetaDmChannel(input.channel)) {
    return null;
  }
  if (input.messagesLoading) {
    return null;
  }

  const lastCustomerSentAt = input.messages.reduce<Date | null>(
    (latest, message) => {
      if (
        message.channel !== input.channel ||
        message.senderType !== "customer"
      ) {
        return latest;
      }
      const sentAt = new Date(message.sentAt);
      if (Number.isNaN(sentAt.getTime())) {
        return latest;
      }
      if (latest === null || sentAt.getTime() > latest.getTime()) {
        return sentAt;
      }
      return latest;
    },
    null,
  );

  const now = input.now ?? Date.now();
  const withinWindow =
    lastCustomerSentAt !== null &&
    now - lastCustomerSentAt.getTime() < SEVEN_DAYS_MS;
  if (withinWindow) {
    return null;
  }

  const label = META_DM_CHANNEL_LABEL[input.channel];
  if (lastCustomerSentAt === null) {
    // Typically a thread opened by a private reply: the page has messaged
    // but the customer hasn't responded yet, so no send window exists.
    return {
      reason: "meta-seven-day-window",
      message: `${label} allows one message to this customer until they respond. You can reply again once they message back.`,
    };
  }
  return {
    reason: "meta-seven-day-window",
    message: `Can't send messages through ${label}'s API more than 7 days after the most recent customer message.`,
  };
}

export function evaluateMetaDmCharacterLimitBlock(input: {
  channel: MessageChannel;
  plainText: string;
}): BlockedSendResult | null {
  if (
    !isMetaDmChannel(input.channel) ||
    input.plainText.length <= META_DM_MAX_CHARACTERS
  ) {
    return null;
  }

  return {
    reason: "meta-dm-character-limit",
    message: `${META_DM_CHANNEL_LABEL[input.channel]} messages are limited to ${META_DM_MAX_CHARACTERS.toLocaleString()} characters.`,
  };
}
